import { useFieldRef } from "@/components/calculator/fields/useFieldRef";
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import { HelperTooltip } from "@/components/calculator/fields/HelperTooltip";

/** Stacked option cards for single-choice questions (how many years, entity type,
 *  audit vs review). Each option can carry a plain-language hint. */
export const ChoiceField = ({ field, value, onChange, error, autoFocus = false, registerRef }) => {
  const wrapRef = useFieldRef({
    path: field.path,
    registerRef,
    autoFocus,
    selector: "button",
  });

  return (
    <div ref={wrapRef} data-testid={`choice-field-${field.path}`}>
      <div className="flex items-start gap-2">
        <p className="text-[15px] font-semibold leading-snug text-foreground">{field.label}</p>
        <HelperTooltip text={field.tooltip} />
      </div>
      {field.help ? (
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{field.help}</p>
      ) : null}

      {field.note ? (
        <p
          data-testid={`choice-note-${field.path}`}
          className="mt-2 rounded-md bg-muted px-3 py-2 text-[13px] leading-relaxed text-muted-foreground"
        >
          {field.note}
        </p>
      ) : null}

      <div
        className="mt-4 space-y-3"
        role="radiogroup"
        aria-label={field.label}
        aria-invalid={error ? "true" : "false"}
      >
        {(field.options || []).map((option) => {
          const isSelected = String(value ?? "") === String(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              data-testid={`choice-${field.path}-${option.value}`}
              onClick={() => onChange(option.value, option.sets)}
              className={`flex w-full items-start gap-3.5 rounded-lg border px-4 py-4 text-left outline-none transition-[border-color,background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-primary/30 ${
                isSelected
                  ? "border-primary bg-[color:var(--tt-teal-mist)] shadow-[inset_0_0_0_1px_var(--tt-teal)]"
                  : "border-input bg-white hover:border-[color:var(--tt-teal-line)] hover:bg-muted"
              }`}
            >
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150 ${
                  isSelected ? "border-primary bg-primary text-white" : "border-input bg-white"
                }`}
                aria-hidden="true"
              >
                {isSelected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-[15px] font-semibold ${
                    isSelected ? "text-[color:var(--tt-teal-deep)]" : "text-foreground"
                  }`}
                >
                  {option.label}
                </span>
                {option.hint ? (
                  <span className="mt-1 block text-[13px] leading-relaxed text-muted-foreground">
                    {option.hint}
                  </span>
                ) : null}
                {option.routesToFallback ? (
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--tt-warn-text)]">
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    We'll put you in touch with our team directly
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <p
          data-testid={`field-error-${field.path}`}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
};
